import React from "react";
import Card from "@mui/material/Card";
import {useSearchParams} from "react-router-dom";
import {useModel23Query, useModels23Query} from "../Model23/query";
import {CardContent, CardHeader, Checkbox, FormControlLabel, InputLabel, NativeSelect} from "@mui/material";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import {useTranslation} from "react-i18next";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import Box from "@mui/material/Box";


import YetizSnowProfile, {
    YetizSnowProfileContainer,
    YetizSnowProfileTools,
    YetizSnowProfileZoom,
    YetizSnowProfileZoomWrapper,
    useYetizSnowProfileMenu,
    useYetizSnowProfileProperties,
    useYetizSnowProfileZoom
} from "../../components/Graph/YetizSnowProfile";


const Models4 = () => {
    const {t} = useTranslation(['models4']);
    const [searchParams, setSearchParams] = useSearchParams();
    const [compareWithModel23Id, setCompareWithModel23Id] = React.useState("");
    const {
        showRamm,
        toggleRamm,
        showStratigraphy,
        toggleStratigraphy,
        showCrystals,
        toggleCrystals,
        showLayerComments,
        toggleLayerComments,
        showFlags,
        toggleFlags,
        yetizProperties,
    } = useYetizSnowProfileProperties();
    const yetizSnowProfileZoom = useYetizSnowProfileZoom({initialZoom: 0.7});

    const {selectedModel23Id: selectedModel23IdParam, ...filters} = Object.fromEntries([...searchParams]);
    const selectedModel23Id = Number.parseInt(selectedModel23IdParam)

    const model23Query = useModel23Query(selectedModel23Id, {keepPreviousData: true});
    const compareWithModel23Query = useModel23Query(
        compareWithModel23Id,
        {keepPreviousData: true, enabled: compareWithModel23Id !== ""}
    );
    const models23Query = useModels23Query(filters, {prefetch: false}); // TODO the prefetch still happens in useModels23Query

    const setSelectedModel23Id = (modelId) => {
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set("selectedModel23Id", modelId);
            return newParams;
        });
    }

    const handleSelectChange = (event) => {
        const modelId = event.target.value;
        if (modelId === "") {
            setSelectedModel23Id(null);
        } else {
            setSelectedModel23Id(modelId);
        }
    }

    const handleCompareWithChange = (event) => {
        const modelId = event.target.value;
        setCompareWithModel23Id(modelId);
    }

    const nextIndex = () => {
        if (!models23Query.data || !selectedModel23Id) {
            return null;
        }
        const currentIndex = models23Query.data.rows.findIndex(model => model.id === selectedModel23Id);
        if (currentIndex === -1 || currentIndex === models23Query.data.rows.length - 1) {
            return null;
        }
        return currentIndex + 1;
    }

    const prevIndex = () => {
        if (!models23Query.data || !selectedModel23Id) {
            return null;
        }
        const currentIndex = models23Query.data.rows.findIndex(model => model.id === selectedModel23Id);
        if (currentIndex <= 0) {
            return null;
        }
        return currentIndex - 1;
    }

    const hasNext = () => nextIndex() !== null;
    const hasPrev = () => prevIndex() !== null;

    const handleNext = () => {
        const nextIdx = nextIndex();
        if (nextIdx === null) {
            return;
        }
        const nextModelId = models23Query.data.rows[nextIdx].id;
        setSelectedModel23Id(nextModelId);
    }

    const handlePrev = () => {
        const prevIdx = prevIndex();
        if (prevIdx === null) {
            return;
        }
        const prevModelId = models23Query.data.rows[prevIdx].id;
        setSelectedModel23Id(prevModelId);
    }

    const models23List = models23Query.data ? models23Query.data.rows : [];

    const selectedMode23Menu = useYetizSnowProfileMenu({model23: model23Query.data});
    const compareWithModel23Menu = useYetizSnowProfileMenu({model23: compareWithModel23Query.data});

    return  <Box sx={{display: "flex", gap: 2, my: 2, flexDirection: "row"}}>
        <Box sx={{width: 320, minWidth: 320, alignSelf: "flexStart"}}>
            <Card sx={{position: "sticky", top: 100}}>
                <CardHeader title={t('title')}/>
                <CardContent sx={{overflowX: "auto"}}>
                    <Stack direction="row" spacing={1}>
                        <IconButton onClick={handlePrev} disabled={!hasPrev()}>
                            <Icon>arrow_backward</Icon>
                        </IconButton>
                        <FormControl fullWidth>
                            <InputLabel variant="standard" htmlFor="uncontrolled-native">
                                {t('selectedModel23')}
                            </InputLabel>
                            <Model23NativeSelect models23List={models23List}
                                                 value={selectedModel23Id}
                                                 onChange={handleSelectChange}
                                                 sx={{flexGrow: 1}}>
                            </Model23NativeSelect>
                        </FormControl>
                        <IconButton onClick={handleNext} disabled={!hasNext()}>
                            <Icon>arrow_forward</Icon>
                        </IconButton>
                    </Stack>

                    <FormControl fullWidth>
                        <InputLabel variant="standard" htmlFor="uncontrolled-native">
                            {t('compareWithModel23')}
                        </InputLabel>
                        <Model23NativeSelect models23List={models23List}
                                             value={compareWithModel23Id}
                                             onChange={handleCompareWithChange}
                                             withEmpty={true}
                                             sx={{flexGrow: 1}}>
                        </Model23NativeSelect>
                    </FormControl>

                    <YetizSnowProfileZoom onZoomIn={yetizSnowProfileZoom.handleZoomIn}
                                onZoomOut={yetizSnowProfileZoom.handleZoomOut}
                                zoom={yetizSnowProfileZoom.zoom}
                                zoomInEnabled={yetizSnowProfileZoom.zoomInEnabled}
                                zoomOutEnabled={yetizSnowProfileZoom.zoomOutEnabled}/>
                    <FormControlLabel
                        control={<Checkbox checked={showStratigraphy} onChange={toggleStratigraphy}
                                           color="info"/>}
                        label={<Typography variant="body2">{t('showStratigraphy')}</Typography>}/>

                    <FormControlLabel
                        control={<Checkbox checked={showRamm} onChange={toggleRamm} color="info"/>}
                        label={<Typography variant="body2">{t('showRamm')}</Typography>}/>
                    <FormControlLabel
                        control={<Checkbox checked={showCrystals} onChange={toggleCrystals} color="info"/>}
                        label={<Typography variant="body2">{t('showCrystals')}</Typography>}/>
                    <FormControlLabel
                        control={<Checkbox checked={showLayerComments} onChange={toggleLayerComments} color="info"/>}
                        label={<Typography variant="body2">{t('showLayerComments')}</Typography>}/>
                    <FormControlLabel
                        control={<Checkbox checked={showFlags} onChange={toggleFlags} color="info"/>}
                        label={<Typography variant="body2">{t('showFlags')}</Typography>}/>
                </CardContent>
            </Card>
        </Box>
        <Box sx={{display: "flex", flexWrap: "wrap", gap: 2, mb: 2, flexDirection: "row", alignItems: "center"}}>
            <YetizSnowProfileContainer width={yetizSnowProfileZoom.width} height={yetizSnowProfileZoom.height}>
                {!model23Query.data ? "Loading" : <>
                    <YetizSnowProfileTools onDownloadPDF={selectedMode23Menu.handleDownloadPDF}
                                 onDownloadPNG={selectedMode23Menu.handleDownloadPNG}/>
                    <YetizSnowProfileZoomWrapper zoom={yetizSnowProfileZoom.zoom}>
                        <YetizSnowProfile profile={model23Query.data}
                                            yetizProperties={yetizProperties}
                                            graphRef={selectedMode23Menu.graphRef} />
                    </YetizSnowProfileZoomWrapper>
                </>
                }
            </YetizSnowProfileContainer>
            {
                compareWithModel23Id === "" ? null :
                    <YetizSnowProfileContainer width={yetizSnowProfileZoom.width} height={yetizSnowProfileZoom.height} >
                        {!compareWithModel23Query.data ? "Loading" : <>
                            <YetizSnowProfileTools onDownloadPDF={compareWithModel23Menu.handleDownloadPDF}
                                         onDownloadPNG={compareWithModel23Menu.handleDownloadPNG}/>
                            <YetizSnowProfileZoomWrapper zoom={yetizSnowProfileZoom.zoom}>
                                <YetizSnowProfile profile={compareWithModel23Query.data}
                                                    yetizProperties={yetizProperties}
                                                    graphRef={compareWithModel23Menu.graphRef} />
                            </YetizSnowProfileZoomWrapper>
                        </>
                        }
                    </YetizSnowProfileContainer>
            }
        </Box>
    </Box>
}

const Model23NativeSelect = ({models23List, onChange, value, withEmpty = false, ...props}) => {
    return <NativeSelect value={value} onChange={onChange} {...props}>
        {withEmpty && <option value={""}></option>}
        {
            models23List.map(model => (
                <option key={model.id} value={model.id}>
                    {model.station.code} - {model.datetime}
                </option>
            ))
        }
    </NativeSelect>
}



export default Models4;